import { EmptyState, FlexColumn, Loading, PaginatedResponse } from '@gorila-shared-ui/components';
import { StyledDivider } from 'baseui/divider';
import { LabelSmall } from 'baseui/typography';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useRecoilValue } from 'recoil';
import useEffectOnce from '../../hooks/useEffectOnce';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getHistoricNotification } from '../../services/notificationService';
import { apiParamsState } from '../../storage/app';
import { notificationSelectedState, selectedNotificationTypeState } from '../../storage/notifications';
import { Notification } from '../../types/notification';
import HistoricNotificationItem from './HistoricNotificationItem';
import NotificationFilters from './NotificationFilters';

export function HistoricNotificationList() {
  const { css, theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [initialLoading, setInitialLoading] = useState(true);
  const [reloadLoading, setReloadLoading] = useState(false);
  const [internalNotificationType, setInternalNotificationType] = useState('');
  const notificationType = useRecoilValue(selectedNotificationTypeState(internalNotificationType));
  const selectedNotification = useRecoilValue(notificationSelectedState);
  const [page, setPage] = useState(1);
  const [paginatedHistoricNotification, setPaginatedHistoricNotification] = useState<PaginatedResponse<Notification>>();
  const { showFailFeedback } = useFeedback();

  const loadHistoricNotifications = async () => {
    if (!apiParams?.clientId || !selectedNotification?.asset?._id || !apiParams?.subClientId) return;
    const { paginatedNotification, error } = await getHistoricNotification(
      apiParams.clientId,
      apiParams.subClientId,
      selectedNotification?.asset?._id,
      page,
      notificationType,
    );
    if (!error && paginatedNotification) {
      if (page > 1) {
        setPaginatedHistoricNotification((prev) => ({
          hasNext: paginatedNotification.hasNext,
          total: paginatedNotification.total,
          items: [...prev!.items, ...paginatedNotification.items],
        }));
      } else {
        setPaginatedHistoricNotification(paginatedNotification);
      }
    } else {
      showFailFeedback(error ?? 'Error al obtener la información');
      setPaginatedHistoricNotification(undefined);
    }
    setInitialLoading(false);
    setReloadLoading(false);
  };

  useEffectOnce(() => {
    loadHistoricNotifications();
    return () => {
      setPage(1);
    };
  });

  useUpdateEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }

    loadHistoricNotifications();
  }, [page]);

  useUpdateEffect(() => {
    setReloadLoading(true);
    setPage(0);
  }, [notificationType]);

  return (
    <FlexColumn
      gap={0}
      classNames={css({ flexGrow: '1', overflow: 'hidden' })}
    >
      {initialLoading && <Loading />}
      {!paginatedHistoricNotification && !initialLoading && (
        <EmptyState title="El activo no tiene mas notificaciones" />
      )}
      {paginatedHistoricNotification && (
        <FlexColumn
          gap={theme.sizing.scale300}
          classNames={css({ flex: '1', overflow: 'hidden' })}
        >
          <StyledDivider
            $size="cell"
            style={{ marginTop: 0, marginBottom: 0, width: '100%' }}
          />
          <LabelSmall>Ultimas {paginatedHistoricNotification?.total} Alarmas</LabelSmall>

          <NotificationFilters
            type={internalNotificationType}
            onChange={setInternalNotificationType}
          />
          {reloadLoading && <Loading />}
          {!!paginatedHistoricNotification.items.length && !reloadLoading && (
            <div
              id="scrollableDiv"
              className={css({ flex: 1, overflow: 'auto' })}
            >
              <InfiniteScroll
                dataLength={paginatedHistoricNotification.items.length ?? 0} //This is important field to render the next data
                next={() => setPage(page + 1)}
                hasMore={paginatedHistoricNotification.hasNext ?? false}
                loader={<Loading />}
                scrollableTarget="scrollableDiv"
                style={{
                  overflow: 'hidden',
                }}
              >
                <FlexColumn gap={'8px'}>
                  {paginatedHistoricNotification.items.map((notification, i) => (
                    <HistoricNotificationItem
                      key={i}
                      notification={notification}
                    />
                  ))}
                </FlexColumn>
              </InfiniteScroll>
            </div>
          )}
          {!paginatedHistoricNotification.items.length && !reloadLoading && (
            <EmptyState title="No se encontraron resultados" />
          )}
        </FlexColumn>
      )}
    </FlexColumn>
  );
}
